import { useAuthReady } from './useAuthReady';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCardIntent, fetchCardIntents, type CardIntent } from '../lib/cards';

const PENDING_POLL_MS = 1500;

export type RechargeToast = {
  kind: 'success' | 'error';
  message: string;
  amount?: number;
  /** Intent createdAt — for the manual-support sheet on failure. */
  at?: string;
};

/** Map of cardId → pending recharge intent (newest wins if several). */
export function usePendingRecharges(): Record<string, CardIntent> {
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const q = useQuery({
    queryKey: ['card-intents', 'recharge', 'pending', userId],
    enabled: ready,
    queryFn: () => fetchCardIntents(initData, { kind: 'recharge', status: 'pending' }),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    // Poll only while something is in flight — Banana settles via webhook.
    refetchInterval: (query) => ((query.state.data?.intents?.length ?? 0) > 0 ? PENDING_POLL_MS : false),
  });

  const map: Record<string, CardIntent> = {};
  for (const intent of q.data?.intents ?? []) {
    if (!intent.cardId) continue;
    // List is newest-first; keep the first (newest) per card.
    if (!(intent.cardId in map)) map[intent.cardId] = intent;
  }
  return map;
}

/**
 * Background watcher: when a pending recharge leaves the pending list (webhook
 * settled it), refetch that card's balance once and surface a toast.
 * Mount once near the wallet root so closing TopUpSheet does not cancel it.
 */
export function useRechargeSettlements(): {
  toast: RechargeToast | null;
  dismiss: () => void;
} {
  const { t } = useTranslation();
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const qc = useQueryClient();
  const [toast, setToast] = useState<RechargeToast | null>(null);

  const q = useQuery({
    queryKey: ['card-intents', 'recharge', 'pending', userId],
    enabled: ready,
    queryFn: () => fetchCardIntents(initData!, { kind: 'recharge', status: 'pending' }),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: (query) => ((query.state.data?.intents?.length ?? 0) > 0 ? PENDING_POLL_MS : false),
  });

  const prevIdsRef = useRef<Set<string>>(new Set());
  const seenRef = useRef(false);
  const handlingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!ready) return;
    const intents = q.data?.intents ?? [];
    const current = new Set(intents.map((i) => i.id));

    // Skip the first snapshot so a page reload mid-pending doesn't toast.
    if (!seenRef.current) {
      if (q.isFetched) {
        prevIdsRef.current = current;
        seenRef.current = true;
      }
      return;
    }

    const settled = [...prevIdsRef.current].filter((id) => !current.has(id));
    prevIdsRef.current = current;

    for (const id of settled) {
      if (handlingRef.current.has(id)) continue;
      handlingRef.current.add(id);
      void (async () => {
        try {
          const { intent } = await fetchCardIntent(initData, id);
          const amount = intent.amount ?? undefined;
          if (intent.status === 'success' && intent.cardId) {
            await Promise.all([
              qc.invalidateQueries({ queryKey: ['card', intent.cardId, 'balance'] }),
              qc.invalidateQueries({ queryKey: ['card', intent.cardId, 'transactions'] }),
              qc.invalidateQueries({ queryKey: ['cards'] }),
            ]);
            setToast({ kind: 'success', message: t('cards.topUpDone'), amount });
            window.setTimeout(() => setToast(null), 2600);
          } else if (intent.status === 'failed') {
            setToast({
              kind: 'error',
              message: t('cards.topUpFailed'),
              amount,
              at: intent.createdAt,
            });
            // Stay until the user dismisses the support sheet (WalletScreen).
          }
        } catch {
          /* ignore — next poll cycle is fine */
        } finally {
          handlingRef.current.delete(id);
        }
      })();
    }
  }, [q.data, q.isFetched, ready, initData, qc, t]);

  return { toast, dismiss: () => setToast(null) };
}
